import {
  type AgencyBrandFieldsFragment,
  BrandElement,
  BrandColorRole,
  BrandLogoVariant
} from '~/api/graphql';
import { computeTint, parseHexChannels, relativeLuminance } from '~/components/library/lib/color';

export type BrandPalette = {
  primaryHex: string;
  secondaryHex: string;
  accentHex: string;
  surfaceHex: string;
  textHex: string;
};

export type PaletteRole = {
  key: keyof BrandPalette;
  label: string;
  description: string;
};

/** Toolbar order, which is the order the chips read: the page, then what goes on it. */
export const PALETTE_ROLES: PaletteRole[] = [
  { key: 'textHex', label: 'Text', description: 'Everything that is read at length' },
  { key: 'surfaceHex', label: 'Page', description: 'The background behind everything' },
  {
    key: 'primaryHex',
    label: 'Primary',
    description: 'Headings, rules, table headers, and the cover'
  },
  { key: 'secondaryHex', label: 'Secondary', description: 'Callout figures and charts' },
  { key: 'accentHex', label: 'Accent', description: 'Rules and highlights, used sparingly' }
];

/** Strips the __typename a GraphQL read carries, so a palette can go straight back as input. */
export const paletteOf = (p: BrandPalette): BrandPalette => ({
  primaryHex: p.primaryHex,
  secondaryHex: p.secondaryHex,
  accentHex: p.accentHex,
  surfaceHex: p.surfaceHex,
  textHex: p.textHex
});

export const samePalette = (a: BrandPalette, b: BrandPalette): boolean =>
  JSON.stringify(paletteOf(a)) === JSON.stringify(paletteOf(b));

export type BrandColorScheme = {
  key: string;
  name: string;
  note: string;
  palette: BrandPalette;
};

export const contrastRatio = (a: string, b: string): number => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};

const toHex = (rgb: [number, number, number]) =>
  '#' + rgb.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('');

const mix = (rgb: [number, number, number], towards: [number, number, number], amount: number) =>
  toHex([
    rgb[0] + (towards[0] - rgb[0]) * amount,
    rgb[1] + (towards[1] - rgb[1]) * amount,
    rgb[2] + (towards[2] - rgb[2]) * amount
  ]);

export const ROW_TINT = 0.12;
export const CALLOUT_TINT = 0.07;

/** A translucent fill flattened onto what sits behind it. */
export const tintOver = (hex: string, backdrop: string, alpha: number): string => {
  const fg = parseHexChannels(hex);
  const bg = parseHexChannels(backdrop);
  if (!fg || !bg) {
    return backdrop;
  }
  return toHex(
    [0, 1, 2].map((i) => fg[i] * alpha + bg[i] * (1 - alpha)) as [number, number, number]
  );
};

/** The renderer mixes 12% of the color into white whatever the page color is. */
export const stripeTint = (hex: string): string => computeTint(hex, ROW_TINT);

/** White or the body color, whichever reads better: the renderer's choice on a fill. */
export const readableOn = (backgroundHex: string, textHex: string): string =>
  contrastRatio('#ffffff', backgroundHex) >= contrastRatio(textHex, backgroundHex)
    ? '#ffffff'
    : textHex;

/** Graphics are held to WCAG's flat 3:1 for non-text (SC 1.4.11), not the text ladder. */
export type UsageKind = 'body' | 'heading' | 'graphic' | 'decoration';

export type Usage = {
  where: string;
  foregroundHex: string;
  backgroundHex: string;
  kind: UsageKind;
};

/**
 * Legible, and comfortably past it. Equal values mean the bar is the whole standard. graphic's 3:1
 * is also accentOnPageRatio in pkg/service/docgen/styles/style_to_brand.go.
 */
const BARS: Record<Exclude<UsageKind, 'decoration'>, [number, number]> = {
  body: [4.5, 7],
  heading: [3, 4.5],
  graphic: [3, 3]
};

const hueOf = (hex: string): number => {
  const rgb = parseHexChannels(hex);
  if (!rgb) {
    return -1;
  }
  const [r, g, b] = rgb.map((c) => c / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const delta = max - Math.min(r, g, b);
  // A gray has no hue to compare, and a near-gray's hue is rounding noise.
  if (delta < 0.08) {
    return -1;
  }
  const h =
    max === r ? ((g - b) / delta) % 6 : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4;
  return (h * 60 + 360) % 360;
};

/**
 * Lightness or hue will do: a test for "invisible", fit only for decoration, never text.
 * convert-styles derives an accent that clears the 1.5 ratio alone, and BARS.graphic's 3:1 on the
 * page (distinctAccentRatio and accentOnPageRatio in pkg/service/docgen/styles/style_to_brand.go),
 * so change them together.
 */
export const distinguishable = (a: string, b: string): boolean => {
  if (contrastRatio(a, b) >= 1.5) {
    return true;
  }
  const [ha, hb] = [hueOf(a), hueOf(b)];
  if (ha < 0 || hb < 0) {
    return false;
  }
  const gap = Math.abs(ha - hb);
  return Math.min(gap, 360 - gap) >= 30;
};

/** The palette color table rows are tinted from, or null when the brand fills no rows. */
export type StripeFill = keyof BrandPalette | null;

const ROLE_KEYS: Record<`${BrandColorRole}`, keyof BrandPalette> = {
  PRIMARY: 'primaryHex',
  SECONDARY: 'secondaryHex',
  ACCENT: 'accentHex',
  TEXT: 'textHex',
  SURFACE: 'surfaceHex'
};

/** The renderer tints rows from the primary until a stripe element names a fill, or none. */
export const stripeFillOf = (elements: ElementStyle[]): StripeFill => {
  const stripe = elements.find((each) => each.element === ELEMENTS.tableStripe);
  if (!stripe) {
    return 'primaryHex';
  }
  return stripe.fillRole ? (ROLE_KEYS[stripe.fillRole] ?? null) : null;
};

/** The combinations the preview draws, not every pair in the palette. */
export const usagesFor = (
  role: keyof BrandPalette,
  p: BrandPalette,
  stripeFill: StripeFill = 'primaryHex'
): Usage[] => {
  const callout = tintOver(p.secondaryHex, p.surfaceHex, CALLOUT_TINT);

  const bodyOnPage: Usage = {
    where: 'Body text on the page',
    foregroundHex: p.textHex,
    backgroundHex: p.surfaceHex,
    kind: 'body'
  };
  const onRow: Usage[] = stripeFill
    ? [
        {
          where: 'Body text on a tinted row',
          foregroundHex: p.textHex,
          backgroundHex: stripeTint(p[stripeFill]),
          kind: 'body'
        }
      ]
    : [];
  const headings: Usage = {
    where: 'Headings on the page',
    foregroundHex: p.primaryHex,
    backgroundHex: p.surfaceHex,
    kind: 'heading'
  };
  const onCover: Usage = {
    where: 'Text on the cover and table headers',
    foregroundHex: readableOn(p.primaryHex, p.textHex),
    backgroundHex: p.primaryHex,
    kind: 'body'
  };
  const figure: Usage = {
    where: 'The figure in a callout',
    foregroundHex: p.secondaryHex,
    backgroundHex: callout,
    kind: 'heading'
  };
  const marks: Usage = {
    where: 'Bullets and callout rules',
    foregroundHex: p.accentHex,
    backgroundHex: p.surfaceHex,
    kind: 'graphic'
  };
  const coverRule: Usage = {
    where: 'The rule on the cover',
    foregroundHex: p.accentHex,
    backgroundHex: p.primaryHex,
    kind: 'decoration'
  };
  // The row belongs to the text and to whichever color the rows are tinted from.
  const rowFor = (key: keyof BrandPalette) => (key === stripeFill ? onRow : []);

  switch (role) {
    case 'textHex':
      return [bodyOnPage, ...onRow];
    // Scoring the page against everything would report one bad accent on two chips.
    case 'surfaceHex':
      return [bodyOnPage, ...rowFor('surfaceHex')];
    case 'primaryHex':
      return [headings, onCover, ...rowFor('primaryHex')];
    case 'secondaryHex':
      return [figure, ...rowFor('secondaryHex')];
    case 'accentHex':
      return [marks, coverRule, ...rowFor('accentHex')];
  }
};

export const READABLE = 70;

/** A mark out of 100: legible lands on READABLE and comfortably past it on 100. */
export const usageScore = (usage: Usage): number => {
  if (usage.kind === 'decoration') {
    return distinguishable(usage.foregroundHex, usage.backgroundHex) ? 100 : 30;
  }

  const ratio = contrastRatio(usage.foregroundHex, usage.backgroundHex);
  const [legible, comfortable] = BARS[usage.kind];

  if (ratio >= legible) {
    return comfortable <= legible
      ? 100
      : Math.round(
          READABLE + (100 - READABLE) * Math.min((ratio - legible) / (comfortable - legible), 1)
        );
  }
  // Capped below the line so a failing combination never rounds up to a passing mark.
  return Math.min(READABLE - 1, Math.round(READABLE * Math.max((ratio - 1) / (legible - 1), 0)));
};

/** A color scores as its worst use. */
export const scoreFor = (
  role: keyof BrandPalette,
  p: BrandPalette,
  stripeFill: StripeFill = 'primaryHex'
): number => Math.min(...usagesFor(role, p, stripeFill).map(usageScore));

export const COMFORTABLE = 85;

export const scoreLabel = (score: number): string => {
  if (score >= COMFORTABLE) {
    return 'Works well';
  }
  if (score >= READABLE) {
    return 'Usable';
  }
  return score >= 40 ? 'Hard to read' : 'Barely visible';
};

/** Lighter or darker shades of the same color that score comfortably, nearest first. */
export const betterColors = (
  role: keyof BrandPalette,
  p: BrandPalette,
  stripeFill: StripeFill = 'primaryHex',
  count = 3
): { hex: string; score: number }[] => {
  const rgb = parseHexChannels(p[role]);
  if (!rgb) {
    return [];
  }
  const current = scoreFor(role, p, stripeFill);
  if (current >= COMFORTABLE) {
    return [];
  }
  const found: { hex: string; score: number }[] = [];

  const steps = 32;
  for (let step = 1; step <= steps && found.length < count; step++) {
    const amount = step / steps;
    for (const hex of [mix(rgb, [0, 0, 0], amount), mix(rgb, [255, 255, 255], amount)]) {
      if (found.length >= count || found.some((c) => c.hex === hex)) {
        continue;
      }
      const score = scoreFor(role, { ...p, [role]: hex }, stripeFill);
      if (score >= COMFORTABLE) {
        found.push({ hex, score });
      }
    }
  }
  return found;
};

/** Every field is optional, and absent leaves it to the document. */
export type ElementStyle = {
  element: `${BrandElement}`;
  fontFamily?: string | null;
  sizePt?: number | null;
  lineHeight?: number | null;
  spaceAbovePt?: number | null;
  spaceBelowPt?: number | null;
  bold?: boolean | null;
  italic?: boolean | null;
  colorRole?: `${BrandColorRole}` | null;
  fillRole?: `${BrandColorRole}` | null;
  linesKeptAtBottom?: number | null;
  linesKeptAtTop?: number | null;
  pageBreakBefore?: boolean | null;
};

export const ELEMENTS = {
  heading1: BrandElement.Heading_1,
  heading2: BrandElement.Heading_2,
  body: BrandElement.Body,
  runningHeader: BrandElement.RunningHeader,
  runningFooter: BrandElement.RunningFooter,
  tableHeader: BrandElement.TableHeader,
  tableCell: BrandElement.TableCell,
  tableStripe: BrandElement.TableStripe,
  chartLabel: BrandElement.ChartLabel
} as const;

export const ELEMENT_LABELS: Record<BrandElement, string> = {
  HEADING_1: 'Section heading',
  HEADING_2: 'Sub-heading',
  BODY: 'Body text',
  RUNNING_HEADER: 'Running header',
  RUNNING_FOOTER: 'Running footer',
  TABLE_HEADER: 'Table header',
  TABLE_CELL: 'Table cell',
  TABLE_STRIPE: 'Table stripe',
  CHART_LABEL: 'Chart label'
};

export const COLOR_ROLES = [
  BrandColorRole.Primary,
  BrandColorRole.Secondary,
  BrandColorRole.Accent,
  BrandColorRole.Text,
  BrandColorRole.Surface
] as const;

export const roleColor = (
  role: string | null | undefined,
  palette: BrandPalette
): string | null => {
  switch (role) {
    case 'PRIMARY':
      return palette.primaryHex;
    case 'SECONDARY':
      return palette.secondaryHex;
    case 'ACCENT':
      return palette.accentHex;
    case 'TEXT':
      return palette.textHex;
    case 'SURFACE':
      return palette.surfaceHex;
    default:
      return null;
  }
};

/** Points. */
export const SIZE_STEPS = [7, 8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36];

/**
 * Where a type scale stops reading as one. The default design sets six sizes, so a seventh is
 * the first one a design added. A step is the larger size over the smaller: under 1.125 the two
 * read as the same size, over 1.6 the heading dominates its page, and two heading steps further
 * apart than 1.25 of each other read as two scales.
 */
export const TYPE_SCALE = { maxSizes: 6, minStep: 1.125, maxStep: 1.6, maxUnevenness: 1.25 };

const sizeOf = (elements: ElementStyle[], defaults: ElementStyle[], element: `${BrandElement}`) =>
  findElement(elements, element).sizePt ?? findElement(defaults, element).sizePt ?? null;

const formatStep = (ratio: number) => `${Math.round(ratio * 100) / 100}×`;

/**
 * What makes a design's type hard to read as a hierarchy: too many sizes, or heading steps too
 * small, too large or uneven. H3 and below are left out because the renderer derives them from
 * the sub-heading and body, so no setting here changes them on its own.
 */
export const typeScaleWarnings = (
  brand: Pick<BrandState, 'elements' | 'header' | 'footer'>,
  defaults: ElementStyle[]
): string[] => {
  const warnings: string[] = [];
  const sized = Object.values(ELEMENTS).filter(
    (element) => shows(element, 'size') && elementOn(element, brand)
  );
  const sizes = [
    ...new Set(
      sized.map((element) => sizeOf(brand.elements, defaults, element)).filter((pt) => pt != null)
    )
  ].sort((a, b) => a - b);
  if (sizes.length > TYPE_SCALE.maxSizes) {
    warnings.push(
      `This design uses ${sizes.length} configured font sizes (${sizes.join(', ')}pt). Consider ${TYPE_SCALE.maxSizes} or fewer; give elements that are close in size the same one.`
    );
  }

  const ladder = [ELEMENTS.body, ELEMENTS.heading2, ELEMENTS.heading1]
    .map((element) => ({
      label: ELEMENT_LABELS[element],
      pt: sizeOf(brand.elements, defaults, element)
    }))
    .filter((rung): rung is { label: string; pt: number } => rung.pt != null);
  const steps: number[] = [];
  for (let i = 1; i < ladder.length; i++) {
    const [lower, upper] = [ladder[i - 1], ladder[i]];
    const ratio = upper.pt / lower.pt;
    steps.push(ratio);
    const smallest = SIZE_STEPS.find((pt) => pt >= lower.pt * TYPE_SCALE.minStep);
    const largest = SIZE_STEPS.filter((pt) => pt <= lower.pt * TYPE_SCALE.maxStep).pop();
    if (ratio <= 1) {
      warnings.push(
        `${upper.label} at ${upper.pt}pt is no larger than ${lower.label.toLowerCase()} at ${lower.pt}pt.${smallest ? ` Try ${smallest}pt or larger.` : ''}`
      );
    } else if (ratio < TYPE_SCALE.minStep) {
      warnings.push(
        `${upper.label} at ${upper.pt}pt is too close to ${lower.label.toLowerCase()} at ${lower.pt}pt to read as a step up.${smallest ? ` Try ${smallest}pt or larger.` : ''}`
      );
    } else if (ratio > TYPE_SCALE.maxStep) {
      warnings.push(
        `${upper.label} at ${upper.pt}pt is ${formatStep(ratio)} ${lower.label.toLowerCase()} at ${lower.pt}pt, a jump that dominates the page.${largest ? ` Try ${largest}pt or smaller.` : ''}`
      );
    }
  }
  const inBounds = steps.every((s) => s >= TYPE_SCALE.minStep && s <= TYPE_SCALE.maxStep);
  if (
    steps.length === 2 &&
    inBounds &&
    Math.max(...steps) / Math.min(...steps) > TYPE_SCALE.maxUnevenness
  ) {
    warnings.push(
      `The steps from body text to sub-heading (${formatStep(steps[0])}) and from sub-heading to section heading (${formatStep(steps[1])}) are uneven. Similar steps read as one scale.`
    );
  }
  return warnings;
};

/** A multiple of the size, so it tracks a size change. */
export const LINE_HEIGHTS = [1, 1.15, 1.2, 1.3, 1.4, 1.45, 1.5, 1.75, 2];

export const SPACE_STEPS = [0, 2, 4, 6, 8, 10, 12, 14, 18, 24, 30, 36];

export const KEEP_LINES = [1, 2, 3, 4];

/** Inches. Below a quarter inch most office printers clip. */
export const MARGIN_STEPS = [0.25, 0.5, 0.625, 0.75, 1, 1.25, 1.5, 2];

/** Off contributes nothing and keeps the values for switching back on. Null is on. */
export type Switchable = { enabled?: boolean | null };

export const partOn = (part: Switchable | null | undefined): boolean => part?.enabled !== false;

/** Which switch governs an element's type. Mirrors Stored.partOn in pkg/service/api/brand/stored.go. */
export const elementOn = (
  element: `${BrandElement}`,
  parts: { header: Switchable | null; footer: Switchable | null }
): boolean => {
  switch (element) {
    case ELEMENTS.runningHeader:
      return partOn(parts.header);
    case ELEMENTS.runningFooter:
      return partOn(parts.footer);
    default:
      return true;
  }
};

export type BrandMarginsState = Switchable & {
  topIn?: number | null;
  rightIn?: number | null;
  bottomIn?: number | null;
  leftIn?: number | null;
};

export type MarginSide = Exclude<keyof BrandMarginsState, keyof Switchable>;

/** Inches. The renderer's margin where the brand sets none or has its margins off. */
export const DEFAULT_MARGIN_IN = 0.75;

export const pageMarginIn = (margins: BrandMarginsState | null, side: MarginSide): number =>
  (partOn(margins) ? margins?.[side] : null) ?? DEFAULT_MARGIN_IN;

/**
 * Points. A running logo with no height of its own fills its margin less a clearance from the
 * paper edge and from the body: an eighth of an inch, or a quarter of a margin too narrow to
 * spare that. Mirrors stampClearancePt in pkg/service/statics/http/pdf_header_footer_stamper.go.
 */
export const slotLogoHeightPt = (maxHeightPt: number | null | undefined, marginIn: number) => {
  if (maxHeightPt != null && maxHeightPt > 0) return maxHeightPt;
  const marginPt = marginIn * 72;
  return marginPt - 2 * Math.min(9, marginPt / 4);
};

export const MARGIN_SIDES: { key: MarginSide; label: string }[] = [
  { key: 'topIn', label: 'Top' },
  { key: 'rightIn', label: 'Right' },
  { key: 'bottomIn', label: 'Bottom' },
  { key: 'leftIn', label: 'Left' }
];

/** Each control left out for an element is one the renderer or Blink discards for it. */
export type ElementControl =
  | 'fontFamily'
  | 'size'
  | 'lineHeight'
  | 'space'
  | 'color'
  | 'fill'
  | 'weight'
  | 'linesKept'
  | 'pageBreakBefore';

const TYPE_ONLY: ElementControl[] = ['fontFamily', 'size', 'lineHeight', 'color', 'weight'];
const RUNNING: ElementControl[] = ['fontFamily', 'size', 'color', 'weight'];

export const CONTROLS_FOR: Record<string, ElementControl[]> = {
  [ELEMENTS.heading1]: [...TYPE_ONLY, 'space', 'pageBreakBefore'],
  [ELEMENTS.heading2]: [...TYPE_ONLY, 'space', 'pageBreakBefore'],
  [ELEMENTS.body]: [...TYPE_ONLY, 'space', 'linesKept'],
  [ELEMENTS.runningHeader]: RUNNING,
  [ELEMENTS.runningFooter]: RUNNING,
  [ELEMENTS.tableHeader]: [...TYPE_ONLY, 'fill'],
  [ELEMENTS.tableCell]: TYPE_ONLY,
  [ELEMENTS.tableStripe]: ['fill'],
  [ELEMENTS.chartLabel]: TYPE_ONLY
};

export const shows = (element: `${BrandElement}`, control: ElementControl): boolean =>
  (CONTROLS_FOR[element] ?? TYPE_ONLY).includes(control);

export type BrandLogoVariantState = `${BrandLogoVariant}`;

export type BrandSlotLogoState = { variant: BrandLogoVariantState; maxHeightPt?: number | null };

export type BrandSlotsState = Switchable & {
  left?: string | null;
  center?: string | null;
  right?: string | null;
  leftLogo?: BrandSlotLogoState | null;
  centerLogo?: BrandSlotLogoState | null;
  rightLogo?: BrandSlotLogoState | null;
};

export type BrandLogoState = {
  fileId: string;
  variant: BrandLogoVariantState;
  url?: string | null;
  name?: string;
};

export type BrandVoiceState = {
  terminology?: string | null;
  tone?: string | null;
  avoid?: string | null;
};

export const LOGO_VARIANTS: { key: BrandLogoVariantState; label: string; hint: string }[] = [
  { key: 'LIGHT', label: 'On light pages', hint: 'Body pages and white covers' },
  { key: 'DARK', label: 'On dark pages', hint: 'Covers painted in your primary' }
];

export const slotLogoKey = (key: SlotPosition): 'leftLogo' | 'centerLogo' | 'rightLogo' =>
  `${key}Logo` as 'leftLogo' | 'centerLogo' | 'rightLogo';

const toSlotLogoInput = (logo: BrandSlotLogoState | null | undefined): BrandSlotLogoState | null =>
  logo ? { variant: logo.variant, maxHeightPt: logo.maxHeightPt ?? null } : null;

export const toLogosInput = (logos: BrandLogoState[]): BrandLogoState[] =>
  logos.map((logo) => ({ fileId: logo.fileId, variant: logo.variant }));

/** Null when nothing is written, so an empty form saves as no voice rather than three blanks. */
export const toVoiceInput = (voice: BrandVoiceState | null | undefined): BrandVoiceState | null => {
  if (!voice) return null;
  const trimmed = {
    terminology: voice.terminology?.trim() || null,
    tone: voice.tone?.trim() || null,
    avoid: voice.avoid?.trim() || null
  };
  return trimmed.terminology || trimmed.tone || trimmed.avoid ? trimmed : null;
};

export type SlotPosition = 'left' | 'center' | 'right';

export const SLOT_POSITIONS: { key: SlotPosition; label: string }[] = [
  { key: 'left', label: 'Left' },
  { key: 'center', label: 'Center' },
  { key: 'right', label: 'Right' }
];

export const toSlotsInput = (slots: BrandSlotsState | null): BrandSlotsState | null =>
  slots
    ? {
        enabled: slots.enabled ?? null,
        left: slots.left ?? null,
        center: slots.center ?? null,
        right: slots.right ?? null,
        leftLogo: toSlotLogoInput(slots.leftLogo),
        centerLogo: toSlotLogoInput(slots.centerLogo),
        rightLogo: toSlotLogoInput(slots.rightLogo)
      }
    : null;

/** Strips the __typename a GraphQL read carries, so the value can go straight back as input. */
export const toMarginsInput = (margins: BrandMarginsState | null): BrandMarginsState | null =>
  margins
    ? {
        enabled: margins.enabled ?? null,
        topIn: margins.topIn ?? null,
        rightIn: margins.rightIn ?? null,
        bottomIn: margins.bottomIn ?? null,
        leftIn: margins.leftIn ?? null
      }
    : null;

export type BrandState = {
  palette: BrandPalette;
  bodyTypeface: string;
  headingTypeface: string;
  elements: ElementStyle[];
  margins: BrandMarginsState | null;
  header: BrandSlotsState | null;
  footer: BrandSlotsState | null;
  logos: BrandLogoState[];
  voice: BrandVoiceState | null;
};

export const brandStateFrom = (brand: AgencyBrandFieldsFragment): BrandState => ({
  palette: paletteOf(brand.palette),
  bodyTypeface: brand.bodyTypeface,
  headingTypeface: brand.headingTypeface,
  elements: brand.elements ?? [],
  margins: toMarginsInput(brand.margins ?? null),
  header: toSlotsInput(brand.header ?? null),
  footer: toSlotsInput(brand.footer ?? null),
  logos: (brand.logos ?? []).map((logo) => ({
    fileId: logo.file.id,
    variant: logo.variant,
    url: logo.file.url,
    name: logo.file.name
  })),
  voice: toVoiceInput(brand.voice ?? null)
});

/** Never sends a null part: the server reads null as "keep", so clearing a part sends it empty. */
export const toBrandInput = (brand: BrandState) => ({
  palette: brand.palette,
  bodyTypeface: brand.bodyTypeface,
  headingTypeface: brand.headingTypeface,
  elements: brand.elements.map(toElementInput),
  margins: toMarginsInput(brand.margins ?? {}),
  header: toSlotsInput(brand.header ?? {}),
  footer: toSlotsInput(brand.footer ?? {}),
  logos: toLogosInput(brand.logos),
  voice: toVoiceInput(brand.voice) ?? { terminology: null, tone: null, avoid: null }
});

/** Compared through the input shape, so undefined, null and a cleared "" all read the same. */
export const sameBrand = (a: BrandState, b: BrandState): boolean =>
  JSON.stringify(toBrandInput(a)) === JSON.stringify(toBrandInput(b));

export const findElement = (elements: ElementStyle[], element: `${BrandElement}`): ElementStyle =>
  elements.find((each) => each.element === element) ?? { element };

export const setElement = (elements: ElementStyle[], next: ElementStyle): ElementStyle[] => {
  const at = elements.findIndex((each) => each.element === next.element);
  if (at === -1) {
    return [...elements, next];
  }
  return elements.map((each, i) => (i === at ? next : each));
};

/** Field by field rather than spread: the input type rejects the query's __typename. */
export const toElementInput = (style: ElementStyle): ElementStyle => ({
  element: style.element,
  fontFamily: style.fontFamily ?? null,
  sizePt: style.sizePt ?? null,
  lineHeight: style.lineHeight ?? null,
  spaceAbovePt: style.spaceAbovePt ?? null,
  spaceBelowPt: style.spaceBelowPt ?? null,
  bold: style.bold ?? null,
  italic: style.italic ?? null,
  colorRole: style.colorRole ?? null,
  fillRole: style.fillRole ?? null,
  linesKeptAtBottom: style.linesKeptAtBottom ?? null,
  linesKeptAtTop: style.linesKeptAtTop ?? null,
  pageBreakBefore: style.pageBreakBefore ?? null
});

/** Compared through the input shape, so absent, null and undefined all read the same. */
export const sameElement = (a: ElementStyle, b: ElementStyle): boolean =>
  JSON.stringify(toElementInput(a)) === JSON.stringify(toElementInput(b));

/** What a running slot prints on one page: the renderer's placeholders filled in. */
export const fillSlot = (text: string | null | undefined, page: number, pages: number): string =>
  (text ?? '')
    .replace(/\{page\}/g, String(page))
    .replace(/\{pages\}/g, String(pages))
    .replace(
      /\{date\}/g,
      new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    )
    .trim();
